import type { components } from '../../api/generated/schema'
import { parseMoney } from '../../api/parseMoney'

/**
 * The only place a `GET /accounts/{id}/statement-cycles` body is read. Money
 * crosses the wire as a string and is parsed ONCE here into integer cents
 * (a non-money string throws — `parseMoney`'s own rule); nothing downstream
 * sums or derives a figure. `start_date`/`end_date` stay `YYYY-MM-DD` strings:
 * they are calendar dates, never instants, and must not be fed to
 * `new Date(...)`.
 */
export type StatementCycle = {
  startDate: string
  endDate: string
  isCurrent: boolean
  spendCents: number
  creditsCents: number
  transactionCount: number
  otherCurrencyCount: number
  currency: string
}

/** Newest first; the open cycle is first. */
export type StatementCycles = { statementDay: number; cycles: StatementCycle[] }

export function toStatementCycles(w: components['schemas']['StatementCyclesResponse']): StatementCycles {
  return {
    statementDay: w.statement_day,
    cycles: w.cycles.map((c) => ({
      startDate: c.start_date,
      endDate: c.end_date,
      isCurrent: c.is_current,
      spendCents: parseMoney(c.spend),
      creditsCents: parseMoney(c.credits),
      transactionCount: c.transaction_count,
      otherCurrencyCount: c.other_currency_count,
      currency: c.currency,
    })),
  }
}

export type StatementErrorKind = 'not_set' | 'not_credit' | 'tz' | null

/**
 * Classifies a statement-cycles error body. `not_set` / `not_credit` come from
 * `detail.status`; an unknown time zone is a BARE STRING `detail` starting
 * `tz:`. Anything else — including FastAPI's array `detail` — is `null`, never
 * a guess. Exact match on the status: a prefix would take a future
 * `statement_day_not_set_yet`.
 */
export function statementErrorKind(body: unknown): StatementErrorKind {
  if (typeof body !== 'object' || body === null) return null
  const detail = (body as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail.startsWith('tz:') ? 'tz' : null
  if (typeof detail !== 'object' || detail === null || Array.isArray(detail)) return null
  const status = (detail as { status?: unknown }).status
  if (status === 'statement_day_not_set') return 'not_set'
  if (status === 'statement_day_not_credit') return 'not_credit'
  return null
}
